import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  directoryApi,
  type CreateRosterBlockRequest,
  type CreateRosterGroupRequest,
  type DriverRosterBlockDto,
  type DriverRosterConfigDto,
  type RosterDriverRow,
  type RosterGroupDto,
  type RosterMonthDto,
  type UpdateRosterBlockRequest,
  type UpdateRosterGroupRequest,
  type UpsertDriverRosterConfigRequest,
} from '../client';

/** Stable query-key factory for roster caches. */
export const rosterKeys = {
  all: ['directory', 'roster'] as const,
  month: (organisationId: string, year: number, month: number) =>
    ['directory', 'roster', 'month', organisationId, year, month] as const,
  groups: (organisationId: string) => ['directory', 'roster', 'groups', organisationId] as const,
  drivers: (organisationId: string) => ['directory', 'roster', 'drivers', organisationId] as const,
  blocks: (organisationId: string, driverResourceId: string, year: number, month: number) =>
    ['directory', 'roster', 'blocks', organisationId, driverResourceId, year, month] as const,
};

/** Fetch the flat list of rostered drivers (for the Drivers management grid). */
export function useDriverRosterConfigs(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<RosterDriverRow[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<RosterDriverRow[], Error>({
    queryKey: organisationId ? rosterKeys.drivers(organisationId) : rosterKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getDriverRosterConfigs(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Fetch the roster grid for an org + month (1-based). Pass `null` org to keep
 * the query disabled (e.g. while the org id is still resolving).
 */
export function useRoster(
  organisationId: string | null,
  year: number,
  month: number,
  options?: Omit<UseQueryOptions<RosterMonthDto, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<RosterMonthDto, Error>({
    queryKey: organisationId
      ? rosterKeys.month(organisationId, year, month)
      : rosterKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getRoster(organisationId, year, month);
    },
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/** Fetch all roster groups for an org (group picker + management). */
export function useRosterGroups(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<RosterGroupDto[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<RosterGroupDto[], Error>({
    queryKey: organisationId ? rosterKeys.groups(organisationId) : rosterKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getRosterGroups(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Fetch a single driver's roster blocks for an org + month (1-based). Disabled
 * until both the org id and driver id are known (e.g. no row selected yet).
 */
export function useDriverRosterBlocks(
  organisationId: string | null,
  driverResourceId: string | null,
  year: number,
  month: number,
  options?: Omit<UseQueryOptions<DriverRosterBlockDto[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<DriverRosterBlockDto[], Error>({
    queryKey:
      organisationId && driverResourceId
        ? rosterKeys.blocks(organisationId, driverResourceId, year, month)
        : rosterKeys.all,
    queryFn: () => {
      if (!organisationId || !driverResourceId) throw new Error('No organisation or driver id');
      return directoryApi.getRosterBlocks(organisationId, driverResourceId, year, month);
    },
    enabled: !!organisationId && !!driverResourceId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/** Any roster write refreshes the whole roster cache (grid + groups). */
function useRosterInvalidator() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: rosterKeys.all });
}

export function useUpsertDriverRosterConfig() {
  const invalidate = useRosterInvalidator();
  return useMutation<DriverRosterConfigDto, Error, UpsertDriverRosterConfigRequest>({
    mutationKey: [...rosterKeys.all, 'config', 'upsert'],
    mutationFn: (body) => directoryApi.upsertDriverRosterConfig(body),
    onSuccess: invalidate,
  });
}

export function useCreateRosterBlock() {
  const invalidate = useRosterInvalidator();
  return useMutation<DriverRosterBlockDto, Error, CreateRosterBlockRequest>({
    mutationKey: [...rosterKeys.all, 'blocks', 'create'],
    mutationFn: (body) => directoryApi.createRosterBlock(body),
    onSuccess: invalidate,
  });
}

export function useUpdateRosterBlock() {
  const invalidate = useRosterInvalidator();
  return useMutation<DriverRosterBlockDto, Error, UpdateRosterBlockRequest>({
    mutationKey: [...rosterKeys.all, 'blocks', 'update'],
    mutationFn: (body) => directoryApi.updateRosterBlock(body),
    onSuccess: invalidate,
  });
}

export function useDeleteRosterBlock() {
  const invalidate = useRosterInvalidator();
  return useMutation<unknown, Error, string>({
    mutationKey: [...rosterKeys.all, 'blocks', 'delete'],
    mutationFn: (id) => directoryApi.deleteRosterBlock(id),
    onSuccess: invalidate,
  });
}

export function useCreateRosterGroup() {
  const invalidate = useRosterInvalidator();
  return useMutation<RosterGroupDto, Error, CreateRosterGroupRequest>({
    mutationKey: [...rosterKeys.all, 'groups', 'create'],
    mutationFn: (body) => directoryApi.createRosterGroup(body),
    onSuccess: invalidate,
  });
}

export function useUpdateRosterGroup() {
  const invalidate = useRosterInvalidator();
  return useMutation<RosterGroupDto, Error, UpdateRosterGroupRequest>({
    mutationKey: [...rosterKeys.all, 'groups', 'update'],
    mutationFn: (body) => directoryApi.updateRosterGroup(body),
    onSuccess: invalidate,
  });
}

export function useDeleteRosterGroup() {
  const invalidate = useRosterInvalidator();
  return useMutation<unknown, Error, string>({
    mutationKey: [...rosterKeys.all, 'groups', 'delete'],
    mutationFn: (id) => directoryApi.deleteRosterGroup(id),
    onSuccess: invalidate,
  });
}
